import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import CityAutocomplete from "@/components/travel/CityAutocomplete";
import { Stars } from "@/components/travel/Stars";
import { motion } from "framer-motion";
import { ArrowLeftRight, Plane, Train, Car, Ship, Bus, HelpCircle, Lock, CheckCircle2, Zap, BellPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import UnlockModal from "@/components/travel/UnlockModal";

const MODE_ICONS = { Avion: Plane, Train: Train, Route: Car, Bateau: Ship, Bus: Bus, Autre: HelpCircle };

export default function Search() {
  const [sp, setSp] = useSearchParams();
  const [origin, setOrigin] = useState({ label: sp.get("origin") || "" });
  const [destination, setDestination] = useState({ label: sp.get("destination") || "" });
  const [dateFrom, setDateFrom] = useState(sp.get("date_from") || "");
  const [dateTo, setDateTo] = useState(sp.get("date_to") || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unlockTrip, setUnlockTrip] = useState(null);
  const [mineContacts, setMineContacts] = useState({});
  const { user } = useAuth();
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (origin.label) params.origin = origin.label;
      if (destination.label) params.destination = destination.label;
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo).toISOString();
      const r = await api.get("/trips/search", { params });
      setResults(r.data);
      if (user) {
        const mc = await api.get("/contacts/mine");
        const m = {};
        mc.data.forEach(c => { m[c.trip_id] = c.contact_id; });
        setMineContacts(m);
      }
    } catch { toast.error("Erreur de recherche"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const swap = () => { const a = origin; setOrigin(destination); setDestination(a); };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="glass-strong rounded-2xl p-4 md:p-5 grid gap-3 md:grid-cols-[1fr_auto_1fr_1fr_1fr_auto]">
        <CityAutocomplete testid="search-origin" value={origin.label} onChange={setOrigin} placeholder="Départ" />
        <button data-testid="search-swap" onClick={swap} className="btn-ghost-glass px-3 rounded-lg"><ArrowLeftRight size={14}/></button>
        <CityAutocomplete testid="search-destination" value={destination.label} onChange={setDestination} placeholder="Arrivée" />
        <input data-testid="search-date-from" type="date" value={dateFrom} onChange={(e)=>setDateFrom(e.target.value)} className="px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700 text-sm outline-none focus:border-[#FF6B6B]"/>
        <input data-testid="search-date-to" type="date" value={dateTo} onChange={(e)=>setDateTo(e.target.value)} className="px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700 text-sm outline-none focus:border-[#FF6B6B]"/>
        <button data-testid="search-submit" onClick={()=>{ setSp({ origin: origin.label, destination: destination.label }); load(); }} className="btn-coral px-5 py-2 rounded-lg text-sm">Chercher</button>
      </motion.div>

      {user && (origin.label || destination.label) && (
        <div className="mt-3 flex justify-end">
          <button data-testid="save-search-btn" onClick={async ()=>{
            try {
              await api.post("/searches", { origin: origin.label, destination: destination.label });
              toast.success("Recherche sauvegardée — vous recevrez une alerte push si un trajet correspond");
            } catch { toast.error("Erreur"); }
          }} className="btn-ghost-glass px-3 py-1.5 rounded-lg text-xs flex items-center gap-1"><BellPlus size={12}/> Alerte moi si un trajet correspond</button>
        </div>
      )}

      <div className="mt-6 text-sm text-slate-400">{loading ? "Recherche…" : `${results.length} trajet(s)`}</div>

      <div className="mt-4 grid gap-3">
        {results.map((t) => {
          const Ic = MODE_ICONS[t.mode] || HelpCircle;
          const alreadyContact = mineContacts[t.trip_id];
          return (
            <motion.div key={t.trip_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="glass rounded-xl p-4 flex flex-col md:flex-row gap-4 md:items-center hover:border-slate-600 border border-slate-800 transition">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center"><Ic size={18} className="text-[#F59E0B]"/></div>
                <div>
                  <div className="text-xs text-slate-400">{t.mode}{t.is_pro && <span className="ml-2 px-1.5 py-0.5 rounded bg-[#3B82F6]/20 text-[#3B82F6]">PRO</span>}</div>
                  <div className="font-semibold text-sm">{t.origin.label} <span className="text-slate-500">→</span> {t.destination.label}</div>
                  <div className="text-xs text-slate-400">{new Date(t.depart_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })} — {new Date(t.arrive_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</div>
                </div>
              </div>
              <div className="flex-1 flex flex-wrap gap-1">
                {t.exclusions?.map((e) => <span key={e} className="text-[10px] px-2 py-0.5 rounded-full border border-slate-700 text-slate-400">🚫 {e}</span>)}
              </div>
              <div className="flex items-center gap-3">
                <button data-testid={`open-profile-${t.trip_id}`} onClick={()=>nav(`/profile/${t.user_id}`)} className="text-left">
                  <div className="text-xs text-slate-400">{t.traveler?.name}</div>
                  <div className="flex items-center gap-1 text-xs"><Stars value={t.traveler?.rating_avg || 0}/> <span className="text-slate-500">({t.traveler?.rating_count || 0})</span></div>
                </button>
                {alreadyContact ? (
                  <button data-testid={`chat-btn-${t.trip_id}`} onClick={()=>nav(`/messages/${alreadyContact}`)} className="btn-ghost-glass px-3 py-2 rounded-lg text-sm flex items-center gap-1">
                    <CheckCircle2 size={14} className="text-emerald-400"/> Débloqué
                  </button>
                ) : user?.is_admin ? (
                  <button data-testid={`admin-force-${t.trip_id}`} onClick={async ()=>{ try { const r = await api.post("/admin/simulate/unlock-any", { trip_id: t.trip_id }); toast.success("Contact débloqué (admin)"); setMineContacts(m=>({...m, [t.trip_id]: r.data.contact_id})); nav(`/messages/${r.data.contact_id}`); } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } }} className="btn-coral px-3 py-2 rounded-lg text-sm flex items-center gap-1">
                    <Lock size={12}/> Débloquer (admin)
                  </button>
                ) : (
                  <button data-testid={`unlock-btn-${t.trip_id}`} onClick={()=> { if (!user) { toast.info("Connectez-vous pour débloquer"); nav("/login"); } else { setUnlockTrip(t); } }} className="btn-coral px-3 py-2 rounded-lg text-sm flex items-center gap-1">
                    <Lock size={12}/> Débloquer 2€
                  </button>
                )}
              </div>
            </motion.div>
          );
        })}
        {!loading && results.length === 0 && (
          <div className="text-center text-slate-500 py-12">
            <Zap className="mx-auto mb-2 text-slate-700" />
            Aucun trajet trouvé. Essayez d'élargir vos critères.
          </div>
        )}
      </div>

      {unlockTrip && <UnlockModal trip={unlockTrip} onClose={()=>setUnlockTrip(null)} onUnlocked={(cid)=>{ setMineContacts(m=>({...m, [unlockTrip.trip_id]: cid})); setUnlockTrip(null); nav(`/messages/${cid}`); }} />}
    </div>
  );
}
